import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { MessageCircle, Plus, Heart, MessageSquare } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import { COMMUNITY, timeAgo } from "@/lib/categories";
import { countriesByProximity, profileCities } from "@/lib/places";
import { useLocation } from "@/context/LocationContext";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { EmojiButton, EmojiPreview } from "@/components/EmojiButton";
import { EmojiText } from "@/lib/emoji";
import { initSeen, unreadCls } from "@/lib/seenPosts";

export default function CommunityPage() {
  const nav = useNavigate();
  const { requireAuth } = useAuth();
  const { loc } = useLocation();
  const sortedCountries = useMemo(() => countriesByProximity(loc.country, loc.coords), [loc.country, loc.coords]);
  const [country, setCountry] = useState("");
  const [city, setCity] = useState("");
  const [topic, setTopic] = useState("");
  const [posts, setPosts] = useState([]);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ country: "Suedi", city: "", topic: "Pyetje & Përgjigje", title: "", body: "" });

  useEffect(() => { if (loc.country) setF((p) => ({ ...p, country: loc.country, city: profileCities(loc.country).includes(loc.city) ? loc.city : "" })); }, [loc.country, loc.city]);

  const load = () => {
    const p = new URLSearchParams();
    if (country) p.set("country", country);
    if (country && city) p.set("city", city);
    if (topic) p.set("topic", topic);
    api.get(`/community/posts?${p.toString()}`).then((r) => { initSeen(r.data); setPosts(r.data); }).catch(() => {});
  };
  useEffect(load, [country, city, topic]);
  useEffect(() => { setCity(""); }, [country]);

  const create = () => requireAuth(async () => {
    if (!f.title) return toast.error("Shkruaj një titull");
    try { await api.post("/community/posts", f); setOpen(false); setF({ ...f, title: "", body: "" }); toast.success("Biseda u publikua!"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  });

  return (
    <div className="gs-fade px-4 pt-4">
      <h1 className="flex items-center gap-2 text-2xl font-extrabold text-slate-900"><MessageCircle className="w-6 h-6 text-cyan-500" />Komunitete</h1>

      <div className="flex gap-2 overflow-x-auto no-scrollbar mt-3 pb-1">
        <button onClick={() => setCountry("")} className={`shrink-0 text-xs font-semibold px-3 py-1.5 rounded-full border ${!country ? "bg-cyan-500 text-white border-cyan-500" : "bg-white text-slate-600 border-slate-200"}`}>Të gjitha shtetet</button>
        {sortedCountries.map((c) => {
          const active = country === c.name;
          const mine = loc.country === c.name;
          return (
          <button key={c.name} data-testid={`country-${c.name}`} onClick={() => setCountry(active ? "" : c.name)} className={`shrink-0 text-xs font-semibold px-3 py-1.5 rounded-full border flex items-center gap-1.5 ${active ? "bg-cyan-500 text-white border-cyan-500" : mine ? "bg-cyan-50 text-cyan-700 border-cyan-400 ring-2 ring-cyan-200" : "bg-white text-slate-600 border-slate-200"}`}><span className="text-sm leading-none">{c.flag}</span>{c.name}</button>
          );
        })}
      </div>
      {country && profileCities(country).length > 0 && (
        <div className="flex gap-2 overflow-x-auto no-scrollbar mt-2 pb-1" data-testid="city-filter">
          <button data-testid="city-all" onClick={() => setCity("")} className={`shrink-0 text-xs font-semibold px-3 py-1.5 rounded-full border ${!city ? "bg-cyan-500 text-white border-cyan-500" : "bg-white text-slate-600 border-slate-200"}`}>Të gjitha qytetet</button>
          {profileCities(country).map((c) => (
            <button key={c} data-testid={`city-${c}`} onClick={() => setCity(city === c ? "" : c)} className={`shrink-0 text-xs font-semibold px-3 py-1.5 rounded-full border ${city === c ? "bg-cyan-500 text-white border-cyan-500" : loc.city === c ? "bg-cyan-50 text-cyan-700 border-cyan-400" : "bg-white text-slate-600 border-slate-200"}`}>{c}</button>
          ))}
        </div>
      )}
      <div className="flex gap-2 overflow-x-auto no-scrollbar mt-2 pb-1">
        {COMMUNITY.topics.map((t) => (
          <button key={t} data-testid={`topic-${t}`} onClick={() => setTopic(topic === t ? "" : t)} className={`shrink-0 text-xs font-semibold px-3 py-1.5 rounded-full border ${topic === t ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200"}`}>{t}</button>
        ))}
      </div>

      <div className="space-y-2 mt-3">
        {posts.map((p) => (
          <button key={p.id} data-testid={`post-${p.id}`} onClick={() => nav(`/komunitete/${p.id}`)} className={`w-full text-left ${unreadCls(p)} rounded-2xl p-4 border shadow-sm hover:shadow-md transition-all`}>
            <div className="flex items-center gap-2 text-xs mb-1.5">
              <span className="bg-cyan-50 text-cyan-600 font-semibold px-2 py-0.5 rounded-full">{p.topic}</span>
              <span className="text-slate-400">{p.city ? `${p.city}, ` : ""}{p.country}</span>
            </div>
            <div className="font-bold text-slate-800">{p.title}</div>
            {p.body && <p className="text-sm text-slate-500 mt-1 line-clamp-2"><EmojiText body={p.body} /></p>}
            <div className="flex items-center gap-4 mt-2 text-xs text-slate-400">
              <span className="flex items-center gap-1"><Heart className="w-3.5 h-3.5" />{p.likes?.length || 0}</span>
              <span className="flex items-center gap-1"><MessageSquare className="w-3.5 h-3.5" />{p.comment_count}</span>
              <span className="ml-auto">{p.author_name} · {timeAgo(p.created_at)}</span>
            </div>
          </button>
        ))}
        {!posts.length && <p className="text-center text-slate-400 py-10">Ende pa biseda në këtë filtër.</p>}
      </div>

      {createPortal(
        <button data-testid="new-post-button" onClick={() => setOpen(true)} className="fixed bottom-24 right-4 md:right-[calc(50%-240px)] bg-cyan-500 hover:bg-cyan-600 text-white rounded-full px-5 py-3.5 shadow-lg shadow-cyan-500/40 flex items-center gap-2 font-semibold z-40">
          <Plus className="w-5 h-5" />Shkruaj një bisedë
        </button>,
        document.body
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-sm rounded-3xl">
          <DialogHeader><DialogTitle>Shkruaj një bisedë</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <select data-testid="post-country" value={f.country} onChange={(e) => setF({ ...f, country: e.target.value, city: "" })} className="bg-white border border-slate-200 rounded-xl h-11 px-3 text-sm outline-none">
                {sortedCountries.map((c) => <option key={c.name} value={c.name}>{c.flag} {c.name}</option>)}
              </select>
              <select data-testid="post-city" aria-label="Qyteti" value={f.city} onChange={(e) => setF({ ...f, city: e.target.value })} className="bg-white border border-slate-200 rounded-xl h-11 px-3 text-sm outline-none min-w-0">
                <option value="">Qyteti</option>
                {profileCities(f.country).map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <select data-testid="post-topic" value={f.topic} onChange={(e) => setF({ ...f, topic: e.target.value })} className="w-full bg-white border border-slate-200 rounded-xl h-11 px-3 text-sm outline-none">
              {COMMUNITY.topics.map((t) => <option key={t}>{t}</option>)}
            </select>
            <Input data-testid="post-title" placeholder="Titulli / pyetja" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} className="rounded-xl h-11" />
            <Textarea data-testid="post-body" placeholder="Shkruaj më shumë..." value={f.body} onChange={(e) => setF({ ...f, body: e.target.value })} className="rounded-xl" />
            <div className="flex flex-wrap items-center gap-1"><EmojiButton inline testid="post-emoji-btn" onPick={(em) => setF((x) => ({ ...x, body: (x.body || "") + em }))} /><EmojiPreview text={f.body} testid="post-emoji-preview" /></div>
            <Button data-testid="submit-post" onClick={create} className="w-full rounded-xl bg-cyan-500 hover:bg-cyan-600 font-semibold h-11">Publiko bisedën</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
